import type { ReactNode } from "react";
import { SafeImage } from "./SafeImage";

/** Navy wash presets. `heavy` is for bright photos (e.g. the office). */
const OVERLAYS: Record<"default" | "heavy", string> = {
  default:
    "linear-gradient(90deg, rgba(13,20,34,0.62) 0%, rgba(13,20,34,0.34) 55%, rgba(13,20,34,0.2) 100%), linear-gradient(180deg, rgba(13,20,34,0.62) 0%, rgba(13,20,34,0.3) 45%, rgba(13,20,34,0.72) 100%)",
  heavy:
    "linear-gradient(90deg, rgba(13,20,34,0.82) 0%, rgba(13,20,34,0.6) 50%, rgba(13,20,34,0.34) 100%), linear-gradient(180deg, rgba(13,20,34,0.6) 0%, rgba(13,20,34,0.32) 45%, rgba(13,20,34,0.7) 100%)",
};

interface PageBannerProps {
  image: string;
  alt: string;
  overlay?: keyof typeof OVERLAYS;
  /** CSS object-position for the photo, e.g. "70% center". */
  objectPosition?: string;
  children: ReactNode;
}

/**
 * Shared page banner — every top-level page hero renders through this
 * so banners are the same size and dimensions across the site (client
 * feedback, Aug 2026). The fixed min-height is the contract: content is
 * vertically centred inside it, so shorter headings no longer produce
 * shorter banners.
 */
export function PageBanner({
  image,
  alt,
  overlay = "default",
  objectPosition,
  children,
}: PageBannerProps) {
  return (
    <section
      data-on-dark
      className="relative isolate flex min-h-[300px] items-center overflow-hidden border-b border-[var(--line)] bg-[var(--navy-deep)] sm:min-h-[340px] md:min-h-[400px] lg:min-h-[440px] short:min-h-[240px]"
    >
      <div className="absolute inset-0">
        <SafeImage
          src={image}
          alt={alt}
          className="h-full w-full object-cover"
          style={objectPosition ? { objectPosition } : undefined}
          fetchPriority="high"
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: OVERLAYS[overlay] }}
        />
      </div>
      <div className="relative container-wide w-full py-12 md:py-14 short:py-8">
        {children}
      </div>
    </section>
  );
}
